import { Link } from "@tanstack/react-router";
import { BadgeCheck, Download, Heart, LayoutDashboard, Square } from "lucide-react";
import type { Listing } from "@cd/core";
import { Badge, Card } from "../../components/ui/primitives";

export const plural = (n: number, one: string) => `${n.toLocaleString()} ${one}${n === 1 ? "" : "s"}`;

export function KindBadge({ kind }: { kind: Listing["kind"] }) {
  const Icon = kind === "dashboard" ? LayoutDashboard : Square;
  return <Badge><Icon className="size-3" /> {kind === "dashboard" ? "Dashboard" : "Widget"}</Badge>;
}

export function VerifiedBadge() {
  return <Badge tone="info"><BadgeCheck className="size-3" /> Verified</Badge>;
}

export function ListingCard({ listing: l, onTag }: { listing: Listing; onTag?: (tag: string) => void }) {
  return (
    <Link to="/marketplace/$id" params={{ id: l.id }} className="block rounded-[14px] outline-none focus-visible:ring-2 focus-visible:ring-accent/50">
      <Card className="flex h-full flex-col p-4 transition-colors hover:border-border-strong">
        <div className="flex flex-wrap items-center gap-1.5">
          <KindBadge kind={l.kind} />
          {l.verified && <VerifiedBadge />}
          {l.installedVersion !== undefined && (l.installedVersion < l.version
            ? <Badge tone="warn">Installed v{l.installedVersion} · v{l.version} out</Badge>
            : <Badge tone="good">Installed</Badge>)}
        </div>
        <h3 className="mt-2 line-clamp-2 text-sm font-semibold">{l.title}</h3>
        <p className="text-xs text-subtle">by {l.publisher}</p>
        {l.description && <p className="mt-1.5 line-clamp-2 text-xs text-muted">{l.description}</p>}
        {l.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {l.tags.map((t) => (
              <button
                key={t}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onTag?.(t);
                }}
                className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] text-muted hover:text-foreground"
              >
                #{t}
              </button>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-subtle">
          <span className="inline-flex items-center gap-1" title="People who installed it"><Download className="size-3" /> {l.installers.toLocaleString()}</span>
          <span className="inline-flex items-center gap-1" title="Likes">
            <Heart className={l.likedByMe ? "size-3 fill-current text-accent" : "size-3"} /> {l.likes.toLocaleString()}
          </span>
        </div>
      </Card>
    </Link>
  );
}
